'use client';

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import cn from 'classnames';
import { analytics } from '@/lib/analytics';
import styles from '@/components/ui/Button/Button.module.css';
import {
  detectPluginOs,
  PLUGIN_DOWNLOADS,
  PLUGIN_OS_ORDER,
  type PluginDownloadSurface,
  type PluginOs,
} from '@/utils/pluginDownloads';

type Props = {
  variant?: 'primary' | 'secondary';
  className?: string;
  /** Stretch to the container instead of shrinking at `md`. */
  fullWidth?: boolean;
  surface: PluginDownloadSurface;
  onDownload?: (os: PluginOs) => void;
};

const PluginDownloadButton = ({
  variant = 'primary',
  className,
  fullWidth = false,
  surface,
  onDownload,
}: Props) => {
  const [os, setOs] = useState<PluginOs>('macos');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const isPrimary = variant === 'primary';
  const tone = isPrimary ? styles.primary : styles.secondary;

  useEffect(() => {
    setOs(detectPluginOs());
  }, []);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const current = PLUGIN_DOWNLOADS[os];

  const track = (next: PluginOs) => {
    analytics.capture('plugin.download.clicked', { os: next, surface });
    onDownload?.(next);
  };

  // Don't use Button.module.css `.root` — its `rounded-xl` and `px-10` beat
  // utilities and round the join / squeeze the caret. Color tokens only.
  const halfClass = cn(
    'inline-flex items-center justify-center gap-2 py-3.5 leading-6 text-center cursor-pointer no-underline rounded-none !border-0',
    tone
  );

  return (
    <div
      className={cn('relative inline-flex w-full', !fullWidth && 'md:w-auto', className)}
      ref={rootRef}
    >
      <div
        className={cn(
          'flex w-full overflow-hidden rounded-xl',
          !isPrimary && 'border border-white'
        )}
      >
        <a
          href={current.url}
          rel="noopener noreferrer"
          className={cn(halfClass, 'flex-1 px-5 text-base')}
          style={{ textDecoration: 'none' }}
          onClick={() => track(os)}
        >
          <Download size={20} className="shrink-0" />
          Download for {current.label}
        </a>
        <span
          aria-hidden
          className={cn('w-px shrink-0 self-stretch', isPrimary ? 'bg-black/50' : 'bg-white')}
        />
        <button
          type="button"
          aria-label="Choose operating system"
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => setOpen((value) => !value)}
          className={cn(halfClass, 'w-12 shrink-0 px-0')}
        >
          <ChevronDown size={20} strokeWidth={2} className="h-5 w-5 shrink-0" />
        </button>
      </div>
      <nav
        aria-label="Plugin downloads"
        className={cn(
          open ? 'block' : 'hidden',
          'absolute z-50 left-0 top-full mt-2 w-[280px] bg-zinc-900 text-white rounded-lg shadow-lg border border-zinc-500 border-opacity-50'
        )}
      >
        <ul className="flex flex-col p-3">
          {PLUGIN_OS_ORDER.map((key) => {
            const item = PLUGIN_DOWNLOADS[key];
            return (
              <li key={key}>
                <a
                  href={item.url}
                  rel="noopener noreferrer"
                  className="py-2 px-3 flex flex-row items-center gap-4 min-w-0 leading-6 font-medium text-white rounded-lg hover:bg-zinc-800 transition ease-in-out duration-75"
                  onClick={() => {
                    track(key);
                    setOs(key);
                    setOpen(false);
                  }}
                >
                  <Download size={20} className="flex-shrink-0" />
                  Download for {item.label}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default PluginDownloadButton;
